import React, { useState } from "react";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import Button from "components/ui/Button";
import { useNavigate } from "react-router-dom";

const opportunity = {
  id: 1,
  company: "Luminary Health Tech",
  tagline: "AI-powered women\'s health diagnostics platform",
  sector: "HealthTech",
  stage: "Series A",
  minInvestment: "$2,500",
  projectedROI: "28-35%",
  womenLed: true,
  winVerified: true,
  daysLeft: 12,
  raised: 68,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17ecd7988-1766993264449.png",
  imageAlt: "Female scientist in white lab coat examining medical diagnostic equipment in modern healthcare laboratory",
  founderName: "Dr. Amara Johnson",
  founderTitle: "CEO & Co-Founder",
  founderAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1fca82876-1763294352035.png",
  founderAvatarAlt: "Professional headshot of African American woman with natural hair wearing navy blazer and confident smile",
  description: "Luminary Health Tech is revolutionizing women\'s preventive care through AI diagnostics that detect conditions 3x earlier than traditional methods."
};

export default function FeaturedOpportunity() {
  const [interested, setInterested] = useState(false);
  const navigate = useNavigate();

  return (
    <section
      className="rounded-xl overflow-hidden"
      style={{
        border: "2px solid var(--color-accent)",
        background: "var(--color-card)",
        boxShadow: "0 0 20px rgba(133,113,77,0.12)"
      }}
      aria-label="Featured Investment Opportunity">
      
      {/* WIN Pick Badge Header */}
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{ background: "var(--color-accent)" }}>
        
        <div className="flex items-center gap-2">
          <Icon name="Star" size={14} color="var(--color-primary)" strokeWidth={2.5} />
          <span className="font-caption font-bold text-xs tracking-wider" style={{ color: "var(--color-primary)" }}>
            WIN PICK OF THE WEEK
          </span>
        </div>
        <span className="font-caption text-xs font-semibold" style={{ color: "var(--color-primary)" }}>
          {opportunity?.daysLeft} days left
        </span>
      </div>
      {/* Image */}
      <div className="relative h-36 md:h-44 overflow-hidden">
        <Image
          src={opportunity?.image}
          alt={opportunity?.imageAlt}
          className="w-full h-full object-cover" />
        
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to top, rgba(0,48,87,0.7) 0%, transparent 60%)" }} />
        
        {opportunity?.womenLed &&
        <div
          className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full"
          style={{ background: "rgba(133,113,77,0.92)" }}>
          
            <Icon name="Crown" size={11} color="var(--color-primary)" strokeWidth={2.5} />
            <span className="font-caption text-xs font-bold" style={{ color: "var(--color-primary)" }}>
              Women-Led
            </span>
          </div>
        }
      </div>
      {/* Content */}
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-base md:text-lg leading-tight" style={{ color: "var(--color-foreground)" }}>
              {opportunity?.company}
            </h3>
            <p className="font-caption text-xs mt-0.5 line-clamp-2" style={{ color: "var(--color-muted-foreground)" }}>
              {opportunity?.tagline}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <span
              className="font-caption text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ background: "rgba(0,48,87,0.08)", color: "var(--color-primary)" }}>
              
              {opportunity?.sector}
            </span>
            <span
              className="font-caption text-xs px-2 py-0.5 rounded-full"
              style={{ background: "var(--color-muted)", color: "var(--color-muted-foreground)" }}>
              
              {opportunity?.stage}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-lg p-2 text-center" style={{ background: "var(--color-muted)" }}>
            <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>Min. Investment</p>
            <p className="font-data font-bold text-sm" style={{ color: "var(--color-accent)" }}>{opportunity?.minInvestment}</p>
          </div>
          <div className="rounded-lg p-2 text-center" style={{ background: "var(--color-muted)" }}>
            <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>Projected ROI</p>
            <p className="font-data font-bold text-sm" style={{ color: "#059669" }}>{opportunity?.projectedROI}</p>
          </div>
        </div>

        {/* Funding Progress */}
        <div className="mb-3">
          <div className="flex justify-between mb-1">
            <span className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>Funding Progress</span>
            <span className="font-data text-xs font-semibold" style={{ color: "var(--color-accent)" }}>{opportunity?.raised}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-muted)" }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${opportunity?.raised}%`, background: "var(--color-accent)" }} />
            
          </div>
        </div>

        {/* Founder */}
        <div className="flex items-center gap-2 mb-4">
          <Image
            src={opportunity?.founderAvatar}
            alt={opportunity?.founderAvatarAlt}
            className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
          
          <div className="min-w-0">
            <p className="font-caption text-xs font-semibold truncate" style={{ color: "var(--color-foreground)" }}>
              {opportunity?.founderName}
            </p>
            <p className="font-caption text-xs truncate" style={{ color: "var(--color-muted-foreground)" }}>
              {opportunity?.founderTitle}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            variant={interested ? "secondary" : "default"}
            size="sm"
            fullWidth
            iconName={interested ? "Check" : "Zap"}
            iconPosition="left"
            iconSize={14}
            onClick={() => setInterested(!interested)}>
            
            {interested ? "Interest Logged" : "Express Interest"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
            iconSize={14}
            onClick={() => navigate("/investment-opportunities")}>
            
            Details
          </Button>
        </div>
      </div>
    </section>);

}